import {
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { AuthService } from '../../../core/services/auth/auth.service';
import { FocusTrapDirective } from '../../../shared/directives/focus-trap/focus-trap.directive';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { PatientPortalUserRequestsService } from '../../services/patient-portal-user-requests.service';

const DENIAL_REASONS = [
  { id: 1, label: 'Cannot verify patient identity' },
  { id: 2, label: 'No matching patient record found' },
  { id: 3, label: 'Suspected fraudulent or unauthorized attempt' },
  { id: 4, label: 'Duplicate or existing linked account' },
] as const;

@Component({
  selector: 'app-deny-request-modal',
  standalone: true,
  imports: [IconComponent, FocusTrapDirective],
  templateUrl: './deny-request-modal.html',
  styleUrl: './deny-request-modal.scss',
})
export class DenyRequestModal implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly authService = inject(AuthService);
  private readonly requestsService = inject(PatientPortalUserRequestsService);

  readonly requestId = input.required<number>();
  readonly version = input.required<number>();
  readonly patientName = input.required<string>();

  readonly cancelled = output<void>();
  readonly denied = output<void>();

  readonly denialReasonId = signal<number | null>(DENIAL_REASONS[0].id);
  readonly denyNoteText = signal('');
  readonly isDenyingRequest = signal(false);
  readonly denyError = signal<string | null>(null);

  readonly denialReasons = DENIAL_REASONS;

  /** The deny action is only allowed once a reason is chosen and no request is in flight. */
  readonly canSubmit = computed(() => this.denialReasonId() !== null && !this.isDenyingRequest());

  ngOnInit(): void {
    setTimeout(() => {
      this.elementRef.nativeElement.querySelector<HTMLElement>('input[type="radio"]')?.focus();
    });
  }

  cancel(): void {
    this.cancelled.emit();
  }

  updateDenialReason(id: number): void {
    this.denialReasonId.set(id);
  }

  updateDenyNote(value: string): void {
    this.denyNoteText.set(value);
  }

  submit(): void {
    const denialReasonId = this.denialReasonId();
    if (this.isDenyingRequest() || denialReasonId === null) return;

    this.isDenyingRequest.set(true);
    this.denyError.set(null);

    const userId = this.authService.currentUser()?.patientPortalAdminUserId ?? 0;
    const note = this.denyNoteText().trim() || null;

    this.requestsService
      .denyRequest(this.requestId(), {
        version: this.version(),
        patientPortalAdminUserId: userId,
        patientPortalUserRequestDenialReasonTypeId: denialReasonId,
        note,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isDenyingRequest.set(false);
          this.denied.emit();
        },
        error: (error) => {
          console.error('Failed to deny request', error);
          this.denyError.set('Unable to deny this request. Please try again.');
          this.isDenyingRequest.set(false);
        },
      });
  }

  trackByDenialReasonId(_: number, reason: { id: number }): number {
    return reason.id;
  }
}
